<template>
  <div>
    <div v-if="$apollo.loading" class="d-flex justify-center mt-8">
      <v-progress-circular indeterminate color="primary"></v-progress-circular>
    </div>
    <div v-else-if="detectionPoint">
      <div class="mb-2 d-flex align-center justify-space-between">
        <h3 class="mr-2">{{ $t('global.eventDetectionPointDetail.title') }}</h3>
        <v-btn
          @click="confirmDeleteModalOpen = true"
          class="ml-2"
          color="error"
          style="height: 27px"
          depressed
        >
          {{ $t('global.delete') }}
        </v-btn>
      </div>
      <div class="values-wrapper mb-4">
        <div class="value">
          <v-btn small icon class="edit-btn" @click="editModalOpen = 'name'">
            <v-icon small>mdi-pencil</v-icon>
          </v-btn>
          <span class="value--headline">{{ this.$t('global.detectionPoint') }}</span>
          <span class="value--text">{{ detectionPoint.name }}</span>
        </div>
      </div>
      <h4>{{ $t('global.descriptions') }}</h4>
      <div class="values-wrapper mb-4">
        <div class="value" v-for="lang of allowedLanguages" :key="lang">
          <v-btn
            small
            icon
            class="edit-btn"
            @click="editModalOpen = `descriptionTranslations.${lang}`"
          >
            <v-icon small>mdi-pencil</v-icon>
          </v-btn>
          <span
            ><v-icon size="18">{{ `$${lang}` }}</v-icon></span
          >
          <span class="value--text">{{ detectionPoint[`${lang}Description`] || '---' }}</span>
        </div>
      </div>
      <div class="mb-4 d-flex align-center flex-wrap">
        <h3 class="mr-5">{{ $t('global.eventDetectionPointDetail.assignedToEventCodes') }}</h3>
        <v-btn height="27" depressed :to="{ name: 'AdminEventCodes' }">{{
          $t('global.manage')
        }}</v-btn>
      </div>
      <div class="d-flex">
        <AssignedToList
          :data="detectionPoint.eventCodes"
          :detailPageName="'AdminEventCodeDetail'"
        />
      </div>
    </div>
    <div v-else class="pa-8">
      <v-alert prominent type="error">
        <v-row align="center">
          <v-col class="grow">
            {{ $t('global.eventDetectionPointDetail.detectionPointNotFound') }}
          </v-col>
          <v-col class="shrink">
            <v-btn @click="$router.back()">{{ $t('global.back') }}</v-btn>
          </v-col>
        </v-row>
      </v-alert>
    </div>

    <template v-if="detectionPoint">
      <EditValueModal
        v-slot="{ validations, value, onInput, errors }"
        :isOpen="editModalOpen === 'name'"
        @modalToggle="editModalOpen = null"
        :heading="`${$t('global.edit')}: ${$t('global.name')}`"
        :validations="requiredValidation"
        :originalValue="detectionPoint.name"
        :submit="updateDetectionPoint"
      >
        <v-text-field
          :value="value"
          @input="onInput"
          :error-messages="errors"
          :label="$t('global.name')"
          @blur="validations.value.$touch()"
          filled
          style="width: 330px"
        ></v-text-field>
      </EditValueModal>
      <EditValueModal
        v-for="lang of allowedLanguages"
        :key="lang"
        v-slot="{ validations, value, onInput, errors }"
        :isOpen="editModalOpen === `descriptionTranslations.${lang}`"
        @modalToggle="editModalOpen = null"
        :heading="`${$t('global.edit')}: ${$t('global.adminEventCode.description')}`"
        :validations="maxLengthValidation"
        :originalValue="detectionPoint[`${lang}Description`]"
        :submit="updateDetectionPoint"
      >
        <v-textarea
          :value="value"
          :error-messages="errors"
          @input="onInput"
          @blur="validations.value.$touch()"
          filled
          counter="255"
          rows="2"
          style="width: 330px"
        >
          <template v-slot:label>
            {{ $t('global.adminEventCode.description') }}
            <v-icon class="ml-1" style="vertical-align: middle">{{ `$${lang}` }}</v-icon>
          </template>
        </v-textarea>
      </EditValueModal>

      <ConfirmationDialog
        v-model="confirmDeleteModalOpen"
        :heading="$t('global.adminDetectionPoints.deleteConfirmationModal.heading')"
        :text="
          $t('global.adminDetectionPoints.deleteConfirmationModal.text', {
            detectionPoint: detectionPoint.name,
          })
        "
        :confirmText="$t('global.delete')"
        confirmBtnColor="error"
        :action="deleteDetectionPoint"
      />
    </template>
  </div>
</template>
<script>
import { required, maxLength } from 'vuelidate/lib/validators'
import { eventDetectionPointAdmin } from '@/graphql/query/eventDetectionPointAdmin'
import AssignedToList from '@/components/Admin/ServiceHints/AssignedToList'
import { detectionPointDelete } from '@/graphql/mutations/detectionPointDelete'
import { detectionPointUpdate } from '@/graphql/mutations/detectionPointUpdate'
import EditValueModal from '@/components/Admin/ServiceHints/EditValueModal'
import ConfirmationDialog from '@/components/ConfirmationDialog'
import { allowedLanguages } from '@/translations'
import set from 'lodash/set'

export default {
  name: 'AdminDetectionPointDetail',
  components: { AssignedToList, EditValueModal, ConfirmationDialog },
  props: {
    id: String,
    inModal: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      editModalOpen: null,
      confirmDeleteModalOpen: false,
      requiredValidation: {
        value: { required },
      },
      maxLengthValidation: {
        value: { maxLength: maxLength(255) },
      },
    }
  },
  apollo: {
    detectionPoint: {
      query: eventDetectionPointAdmin,
      variables() {
        return {
          id: this.id,
          lang: this.$i18n.locale,
        }
      },
      update(data) {
        return data.eventDetectionPointAdmin
      },
    },
  },
  methods: {
    async updateDetectionPoint(value) {
      const key = this.editModalOpen
      try {
        const newValue = value?.trim() === '' ? null : value

        const data = {
          detectionPointId: this.id,
          name: this.detectionPoint.name,
          descriptionTranslations: Object.fromEntries(
            allowedLanguages.map((lang) => [lang, this.detectionPoint[`${lang}Description`]]),
          ),
        }

        // update key
        set(data, key, newValue)

        await this.$apollo.mutate({
          mutation: detectionPointUpdate,
          variables: data,
          refetchQueries: ['eventDetectionPointAdmin'],
        })

        this.$toast.success(
          this.$t('global.adminDetectionPoints.updateDetectionPointAction.success'),
        )
        this.editModalOpen = null
      } catch (error) {
        console.error(error)
        const parsedError = error.message?.replace('GraphQL error:', '').trim()
        this.$toast.error(
          parsedError === 'DETECTION_POINT_UPDATE_NAME_TAKEN'
            ? this.$t('global.nameTakenError')
            : this.$t('global.adminDetectionPoints.updateDetectionPointAction.failure'),
        )
      }
    },
    async deleteDetectionPoint() {
      try {
        await this.$apollo.mutate({
          mutation: detectionPointDelete,
          variables: {
            detectionPointId: this.id,
          },
          refetchQueries: this.inModal ? ['serviceHintsAdminOverview'] : [],
        })

        this.$toast.success(
          this.$t('global.adminDetectionPoints.deleteDetectionPointAction.success'),
        )
        if (this.inModal) {
          this.$emit('onDelete')
        } else {
          this.$router.push({ name: 'AdminDetectionPoints' })
        }
      } catch (error) {
        console.error(error)
        this.$toast.error(this.$t('global.adminDetectionPoints.deleteDetectionPointAction.failure'))
      }
    },
  },
  computed: {
    allowedLanguages() {
      return allowedLanguages
    },
  },
}
</script>
<style lang="less" scoped>
@import '~@/assets/less/variables.less';

.values-wrapper {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  grid-gap: 5px;
}

.value {
  padding: 16px;
  background-color: @color-tile-background;
  position: relative;

  &--headline {
    display: block;
    opacity: 0.5;
    font-size: 12px;
    margin-right: 20px;
  }

  &--text {
    display: block;
    font-size: 14px;
    font-weight: bold;
    word-break: break-word;
  }

  .edit-btn {
    position: absolute;
    top: 5px;
    right: 5px;
  }
}
</style>
